import { useReducer } from 'react'
import { GroupedFieldValues } from '../../types'

type Action =
  | {
      type?: 'merge'
      payload: Partial<GroupedFieldValues>
    }
  | {
      type?: 'set'
      payload: GroupedFieldValues
    }

const makeValuesReducer =
  (initialGroupedFieldValues: GroupedFieldValues) =>
  (state: GroupedFieldValues, action: Action | null): GroupedFieldValues => {
    if (action === null) {
      return initialGroupedFieldValues
    }

    if (action.type === 'set') {
      return action.payload
    }

    const modifiedGroups = Object.fromEntries(
      Object.entries(action.payload).map(([actionGroupKey, actionGroup]) => [
        actionGroupKey,
        actionGroup ? { ...state[actionGroupKey], ...actionGroup } : undefined
      ])
    )

    let groupIndex = 1
    const stateWithoutRemovedGroups = Object.entries({
      ...state,
      ...modifiedGroups
    }).reduce((groups, [, value]) => {
      if (value !== undefined) {
        groups[groupIndex] = value
        groupIndex++
      }
      return groups
    }, {} as GroupedFieldValues)

    return stateWithoutRemovedGroups
  }

const useGroupedFieldValues = (
  initialGroupedFieldValues: GroupedFieldValues
) => {
  const fieldValuesReducer = makeValuesReducer(initialGroupedFieldValues)

  const [groupedFieldValues, handleChange] = useReducer(
    fieldValuesReducer,
    initialGroupedFieldValues
  )

  return {
    groupedFieldValues,
    handleChange
  }
}

export default useGroupedFieldValues
